import { Add as AddIcon, CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";
import { Button, Slider, TextField } from "@mui/material";
import { memo, useEffect, useState } from "react";

import { formatter } from "../../../utils/functions";

import ActiveFilterIndicator from "./ActiveFilterIndicator";

const MIN_RANGE = 0.0;
const MAX_RANGE = process.env.REACT_APP_RANGE_SLIDER_MAX || 10000000.0;
const DEFAULT_MIN_VALUE = MIN_RANGE;
const DEFAULT_MAX_VALUE = 50000.0;

const RangeSlider = ({
  bulkUpdateFilters,
  fieldName,
  isActive,
  maxRange = MAX_RANGE,
  maxValue,
  minRange = MIN_RANGE,
  minValue,
  title,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState([0, 0]);

  useEffect(() => {
    if (isEditing) {
      return;
    }
    setValues([+minValue || DEFAULT_MIN_VALUE, +maxValue || DEFAULT_MAX_VALUE]);
  }, [isEditing, minValue, maxValue]);

  const handleChange = (_, newValues) => {
    setValues(newValues);
  };

  const handleMinInputChange = (event) => {
    const newMin = Number(event.target.value);
    setValues([newMin, values[1]]);
  };

  const handleMaxInputChange = (event) => {
    const newMax = Number(event.target.value);
    setValues([values[0], newMax]);
  };

  const handleUpdateFilters = () => {
    const [newMin, newMax] = values;

    bulkUpdateFilters([
      [fieldName + "__gte", newMin],
      [fieldName + "__lte", newMax],
    ]);
    setIsEditing(false);
  };

  function handleClear() {
    bulkUpdateFilters([
      [fieldName + "__gte", undefined],
      [fieldName + "__lte", undefined],
    ]);
    setIsEditing(false);
    setValues([DEFAULT_MIN_VALUE, DEFAULT_MAX_VALUE]);
  }

  const generateTooltipContent = () => {
    if (maxValue) {
      return (
        <div className="flex items-center w-full gap-2 space-between flex-nowrap">
          <div className="w-full">
            <div className="flex flex-row items-baseline">
              <span className="pr-2 text-black">{title}</span>
              <span> {isActive && <ActiveFilterIndicator />}</span>
            </div>
            <div className="flex items-center justify-between gap-1" data-testid="non-edit-value">
              <span className="text-sky-600">{`${formatter.format(
                values[0]
              )} - ${formatter.format(values[1])}`}</span>
            </div>
          </div>
          <Button
            variant="text"
            onClick={handleClear}
            className="!w-fit rounded-md p-1 text-sm text-red-600 hover:bg-transparent hover:text-red-400"
          >
            <CloseOutlinedIcon />
          </Button>
        </div>
      );
    } else {
      return (
        <div className="flex justify-between w-full">
          <div className="flex flex-row items-baseline">
            <span className="pr-2 text-black">{title}</span>
            <span> {isActive && <ActiveFilterIndicator />}</span>
          </div>
          <AddIcon className="text-sky-600" />
        </div>
      );
    }
  };

  if (!isEditing) {
    return (
      <Button
        onClick={() => setIsEditing(true)}
        className="justify-between w-full p-0 mb-2 text-left normal-case"
        data-testid="edit-button"
      >
        {generateTooltipContent()}
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap w-full mb-2">
      <Button
        onClick={() => setIsEditing(false)}
        className="justify-between w-full p-0 text-left normal-case"
      >
        <span className="w-full text-sm font-medium text-black">{title}</span>
      </Button>
      <div className="flex w-full gap-1">
        <div className="flex flex-[1_1_auto] flex-col gap-1">
          <div className="pl-4 pr-2" data-testid="range-slider">
            <Slider
              value={values}
              min={minRange}
              step={1}
              max={maxRange}
              getAriaValueText={(value) => formatter.format(value)}
              valueLabelFormat={(value) => formatter.format(value)}
              onChange={handleChange}
              valueLabelDisplay="auto"
              size="small"
            />
          </div>
          <div className="flex items-center justify-between gap-1">
            <TextField
              label="Min"
              value={values[0]}
              onChange={handleMinInputChange}
              type="number"
              className="w-full"
              size="small"
              InputProps={{ inputProps: { min: 0, max: values[1] } }}
              data-testid="min-input"
            />
            -
            <TextField
              label="Max"
              value={values[1]}
              onChange={handleMaxInputChange}
              type="number"
              className="w-full"
              size="small"
              InputProps={{ inputProps: { min: values[0], max: maxRange } }}
              data-testid="max-input"
            />
          </div>
        </div>
        <div className="grid items-end justify-center grid-rows-2 gap-1">
          <Button
            onClick={() => handleUpdateFilters()}
            className="text-sm text-purple-600 rounded-md hover:bg-purple-600 hover:text-white"
          >
            Save
          </Button>
          <Button
            onClick={handleClear}
            className="text-sm text-red-600 rounded-md hover:bg-red-600 hover:text-white"
          >
            <CloseOutlinedIcon />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default memo(RangeSlider);
